'use client'

import React from 'react'
import { BaseDialog, BaseDialogProps } from './Dialog'
import { Loader2 } from 'lucide-react'
import { cn } from '../utils/styles'

// Button component for form actions
const Button = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link' | 'success'
    size?: 'default' | 'sm' | 'lg' | 'icon'
  }
>(({ className, variant = 'default', size = 'default', ...props }, ref) => {
  const variants = {
    default: 'bg-primary text-primary-foreground hover:bg-primary/90',
    destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
    outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
    secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
    ghost: 'hover:bg-accent hover:text-accent-foreground',
    link: 'text-primary underline-offset-4 hover:underline',
    success: 'bg-green-600 text-white hover:bg-green-700',
  }

  const sizes = {
    default: 'h-10 px-4 py-2',
    sm: 'h-9 rounded-md px-3',
    lg: 'h-11 rounded-md px-8',
    icon: 'h-10 w-10',
  }

  return (
    <button
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
        variants[variant],
        sizes[size],
        className
      )}
      ref={ref}
      {...props}
    />
  )
})
Button.displayName = 'Button'

export interface FormDialogProps extends Omit<BaseDialogProps, 'children' | 'footer'> {
  // Form handling
  onSubmit: (e: React.FormEvent) => void | Promise<void>
  onCancel?: () => void

  // Button configuration
  submitText?: string
  cancelText?: string
  submitVariant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link' | 'success'

  // Loading states
  isSubmitting?: boolean
  submittingText?: string

  // Form validation
  isValid?: boolean
  canSubmit?: boolean

  // Layout options
  hideCancel?: boolean
  hideCancelOnSubmitting?: boolean
  submitOnEnter?: boolean

  // Form content
  children: React.ReactNode

  // Additional footer content
  footerContent?: React.ReactNode
}

export function FormDialog({
  onSubmit,
  onCancel,
  submitText = 'Save',
  cancelText = 'Cancel',
  submitVariant = 'default',
  isSubmitting = false,
  submittingText = 'Saving...',
  isValid = true,
  canSubmit = true,
  hideCancel = false,
  hideCancelOnSubmitting = true,
  submitOnEnter = true,
  children,
  footerContent,
  ...dialogProps
}: FormDialogProps) {
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isSubmitting || !canSubmit) return

    try {
      await onSubmit(e)
      // Let the parent component handle closing the dialog
    } catch (error) {
      console.error('Form submission failed:', error)
    }
  }

  const handleCancel = () => {
    if (isSubmitting && hideCancelOnSubmitting) return
    onCancel?.()
    dialogProps.onOpenChange(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (submitOnEnter && e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSubmit(e as any)
    }
  }

  const showCancel = !hideCancel && (!isSubmitting || !hideCancelOnSubmitting)
  const submitDisabled = isSubmitting || !isValid || !canSubmit

  const footer = (
    <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 gap-2">
      {footerContent}

      {showCancel && (
        <Button
          type="button"
          variant="outline"
          onClick={handleCancel}
          disabled={isSubmitting && hideCancelOnSubmitting}
        >
          {cancelText}
        </Button>
      )}

      <Button
        type="submit"
        variant={submitVariant}
        disabled={submitDisabled}
        form="form-dialog-form"
      >
        {isSubmitting ? (
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>{submittingText}</span>
          </div>
        ) : (
          submitText
        )}
      </Button>
    </div>
  )

  return (
    <BaseDialog
      {...dialogProps}
      footer={footer}
      showCloseButton={!isSubmitting}
    >
      <form
        id="form-dialog-form"
        onSubmit={handleSubmit}
        onKeyDown={handleKeyDown}
        className="space-y-4"
      >
        {children}
      </form>
    </BaseDialog>
  )
}

// Specialized form dialogs
export interface CreateDialogProps extends Omit<FormDialogProps, 'title' | 'submitText'> {
  entityName: string
  title?: string
  submitText?: string
}

export function CreateDialog({
  entityName,
  title,
  submitText,
  ...props
}: CreateDialogProps) {
  const defaultTitle = title || `Create ${entityName}`
  const defaultSubmitText = submitText || 'Create'

  return (
    <FormDialog
      {...props}
      title={defaultTitle}
      submitText={defaultSubmitText}
      submitVariant="default"
    />
  )
}

export interface EditDialogProps extends Omit<FormDialogProps, 'title' | 'submitText'> {
  entityName: string
  title?: string
  submitText?: string
}

export function EditDialog({
  entityName,
  title,
  submitText,
  ...props
}: EditDialogProps) {
  const defaultTitle = title || `Edit ${entityName}`
  const defaultSubmitText = submitText || 'Save Changes'

  return (
    <FormDialog
      {...props}
      title={defaultTitle}
      submitText={defaultSubmitText}
      submitVariant="default"
    />
  )
}

// Input components for forms
export const Input = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>(({ className, type, ...props }, ref) => {
  return (
    <input
      type={type}
      className={cn(
        'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
        className
      )}
      ref={ref}
      {...props}
    />
  )
})
Input.displayName = 'Input'

export const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(({ className, ...props }, ref) => {
  return (
    <textarea
      className={cn(
        'flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
        className
      )}
      ref={ref}
      {...props}
    />
  )
})
Textarea.displayName = 'Textarea'

export const Label = React.forwardRef<
  HTMLLabelElement,
  React.LabelHTMLAttributes<HTMLLabelElement>
>(({ className, ...props }, ref) => (
  <label
    ref={ref}
    className={cn(
      'text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
      className
    )}
    {...props}
  />
))
Label.displayName = 'Label'

// Form field wrapper
export interface FormFieldProps {
  label?: string
  error?: string
  required?: boolean
  description?: string
  children: React.ReactNode
  className?: string
}

export function FormField({
  label,
  error,
  required,
  description,
  children,
  className,
}: FormFieldProps) {
  return (
    <div className={cn('space-y-2', className)}>
      {label && (
        <Label>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
      )}
      {children}
      {description && !error && (
        <p className="text-sm text-muted-foreground">{description}</p>
      )}
      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
    </div>
  )
}

export default FormDialog